import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// =================================================================
// SCOPE DOCUMENT EDITOR
// One unified rich-text editor where the user types the entire scope
// of work as a free-form document. Inline #trade and @product/@person
// tag pills can be inserted at the cursor — the "By Trade" and
// "By Room" lists on the builder side are auto-generated by parsing
// the resulting HTML.
// =================================================================

// Trade colors (must stay in sync with builder portal)
export const TRADE_COLORS = {
  'DEMOLITION': '#EF4444', 'FRAMING': '#F97316', 'ELECTRICAL': '#EAB308', 'PLUMBING': '#3B82F6',
  'HVAC': '#6366F1', 'DRYWALL': '#A3A3A3', 'PAINT': '#EC4899', 'TILE': '#14B8A6', 'FLOORING': '#8B5CF6',
  'CABINETRY': '#D97706', 'COUNTERTOPS': '#7C3AED', 'MILLWORK': '#B45309',
  'TRIM CARPENTER': '#92400E', 'HARDWARE': '#78716C',
  'GLASS & MIRRORS': '#06B6D4', 'APPLIANCES': '#64748B', 'FIXTURES': '#0EA5E9', 'ROOFING': '#DC2626',
  'EXTERIOR': '#059669', 'LANDSCAPING': '#16A34A', 'GENERAL': '#6B7280',
};

export const DEFAULT_TRADES = Object.keys(TRADE_COLORS);

// Statuses that put an item in CHECKLIST view vs FFE view
const CHECKLIST_STATUSES = new Set([
  'ORDER SAMPLES', 'SAMPLES ARRIVED', 'ASK NEIL', 'ASK CHARLENE', 'ASK JALA',
  'GET QUOTE', 'WAITING ON QT', 'READY FOR PRESENTATION',
  'TO BE SELECTED', 'RESEARCHING', 'PENDING APPROVAL', '', null,
]);
const isChecklistItem = (status) => CHECKLIST_STATUSES.has(status || '');

// =================================================================
// Custom Quill blots for inline tag pills
// IMPORTANT: We apply styling via CSS classes ONLY, not inline styles,
// because Quill's format matchers will detect inline styles like
// font-weight/color/background and apply those as separate formats —
// causing color/style bleed into adjacent text.
// =================================================================
const BLOT_INLINE_KEY = 'blots/inline';
const Inline = Quill.import(BLOT_INLINE_KEY);

class TradeTagBlot extends Inline {
  static create(value) {
    const node = super.create();
    const trade = (value && value.trade) || value || 'GENERAL';
    const color = (value && value.color) || TRADE_COLORS[trade] || '#6B7280';
    node.setAttribute('data-trade', trade);
    node.setAttribute('data-tag', 'trade');
    node.setAttribute('data-color', color);
    node.setAttribute('contenteditable', 'false');
    // Use CSS custom property so Quill's format matchers won't pick it up
    node.style.setProperty('--pill-bg', color);
    return node;
  }
  static formats(node) {
    return { trade: node.getAttribute('data-trade'), color: node.getAttribute('data-color') };
  }
}
TradeTagBlot.blotName = 'tradeTag';
TradeTagBlot.tagName = 'span';
TradeTagBlot.className = 'qe-tag-trade';

class ProductTagBlot extends Inline {
  static create(value) {
    const node = super.create();
    const id = (value && value.id) || '';
    const name = (value && value.name) || id;
    const room = (value && value.room) || '';
    const source = (value && value.source) || 'FFE';
    node.setAttribute('data-product-id', id);
    node.setAttribute('data-product-name', name);
    node.setAttribute('data-product-room', room);
    node.setAttribute('data-product-source', source);
    node.setAttribute('data-tag', 'product');
    node.setAttribute('contenteditable', 'false');
    return node;
  }
  static formats(node) {
    return {
      id: node.getAttribute('data-product-id'),
      name: node.getAttribute('data-product-name'),
      room: node.getAttribute('data-product-room'),
      source: node.getAttribute('data-product-source') || 'FFE',
    };
  }
}
ProductTagBlot.blotName = 'productTag';
ProductTagBlot.tagName = 'span';
ProductTagBlot.className = 'qe-tag-product';

class PersonTagBlot extends Inline {
  static create(value) {
    const node = super.create();
    const name = (value && value.name) || value || '';
    const role = (value && value.role) || '';
    node.setAttribute('data-person', name);
    node.setAttribute('data-role', role);
    node.setAttribute('data-tag', 'person');
    node.setAttribute('contenteditable', 'false');
    return node;
  }
  static formats(node) {
    return { name: node.getAttribute('data-person'), role: node.getAttribute('data-role') };
  }
}
PersonTagBlot.blotName = 'personTag';
PersonTagBlot.tagName = 'span';
PersonTagBlot.className = 'qe-tag-person';

// Register once (guarded so HMR doesn't re-register and spam warnings)
if (!Quill.imports['formats/tradeTag']) Quill.register(TradeTagBlot);
if (!Quill.imports['formats/productTag']) Quill.register(ProductTagBlot);
if (!Quill.imports['formats/personTag']) Quill.register(PersonTagBlot);

const FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'indent',
  'link', 'tradeTag', 'productTag', 'personTag',
];

const MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['clean'],
  ],
  clipboard: { matchVisual: false },
};

// =================================================================
// Main Component
// =================================================================
export default function ScopeDocumentEditor({
  value,
  onChange,
  rooms = [],
  contacts = [],
  customTrades = [],
  onAddCustomTrade,
  onRemoveCustomTrade,
  newTradeName,
  setNewTradeName,
}) {
  const quillRef = useRef(null);
  const [showTradeMenu, setShowTradeMenu] = useState(false);
  const [showProductMenu, setShowProductMenu] = useState(false);
  const [showPersonMenu, setShowPersonMenu] = useState(false);
  const [showRoomMenu, setShowRoomMenu] = useState(false);
  const [tradeFilter, setTradeFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [toast, setToast] = useState(null);
  const [currentRoom, setCurrentRoom] = useState(null); // {name, color}
  // Inline autocomplete state (triggered by #, @ or 🏷)
  const [mention, setMention] = useState(null);
  const mentionRef = useRef(null);
  useEffect(() => { mentionRef.current = mention; }, [mention]);

  // Track the "current room" — the most recent H1/H2/H3 above the user's
  // viewport (or cursor). Updates live as they scroll OR move the cursor so
  // the sticky room bar always reflects what room they're in.
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    // Map room name → color for the sticky bar background
    const colorByName = {};
    rooms.forEach(r => { if (r && r.name) colorByName[r.name.trim().toUpperCase()] = r.color || '#D4A574'; });

    const update = () => {
      const root = editor.root;
      const headings = Array.from(root.querySelectorAll('h1, h2, h3'));
      if (headings.length === 0) { setCurrentRoom(null); return; }
      // Only show the room bar when the editor is in the viewport
      const editorRect = editor.scroll.domNode.getBoundingClientRect();
      // If editor is below viewport bottom OR above viewport top → hide
      if (editorRect.top > window.innerHeight - 50 || editorRect.bottom < 100) {
        setCurrentRoom(null);
        return;
      }
      // Reference Y line where the bar sits (top of viewport + offset for any app chrome)
      const referenceY = 100;
      let active = null;
      for (const h of headings) {
        const r = h.getBoundingClientRect();
        if (r.top <= referenceY) active = h;
        else break;
      }
      if (!active) { setCurrentRoom(null); return; }
      const name = (active.textContent || '').trim().toUpperCase();
      setCurrentRoom({ name, color: colorByName[name] || '#D4A574' });
    };

    update();
    const onScroll = () => requestAnimationFrame(update);
    const onSel = () => requestAnimationFrame(update);
    window.addEventListener('scroll', onScroll, true);
    editor.on('text-change', onSel);
    editor.on('selection-change', onSel);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      editor.off('text-change', onSel);
      editor.off('selection-change', onSel);
    };
  }, [rooms, value]);
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const onKeyUp = (e) => {
      if (e.key !== 'Enter') return;
      const sel = editor.getSelection();
      if (!sel) return;
      const [line] = editor.getLine(sel.index);
      if (!line) return;
      const formats = line.formats ? line.formats() : {};
      if (formats.header) {
        editor.formatLine(sel.index, 0, 'header', false, 'user');
      }
    };
    editor.root.addEventListener('keyup', onKeyUp);
    return () => editor.root.removeEventListener('keyup', onKeyUp);
  }, []);

  // Auto-dismiss toast after 1.5s
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 1500);
    return () => clearTimeout(id);
  }, [toast]);

  // Keep the autocomplete popup glued to the cursor on scroll/resize
  useEffect(() => {
    if (!mention) return;
    const reposition = () => {
      const editor = quillRef.current?.getEditor();
      if (!editor) return;
      const sel = editor.getSelection();
      if (!sel) return;
      const bounds = editor.getBounds(sel.index);
      const rootRect = editor.root.getBoundingClientRect();
      setMention(prev => prev ? ({
        ...prev,
        top: rootRect.top + bounds.top + bounds.height + 6,
        left: Math.min(rootRect.left + bounds.left, window.innerWidth - 340),
      }) : null);
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mention?.triggerIndex]);

  const allTrades = [...DEFAULT_TRADES, ...customTrades];

  // Flatten product list for tag picker — include source (FFE vs CHECKLIST) per item
  const allProducts = rooms.flatMap(r =>
    (r.categories || []).flatMap(c =>
      (c.subcategories || []).flatMap(sub =>
        (sub.items || []).map(item => ({
          id: item.id,
          name: item.name || '(unnamed)',
          room: r.name,
          source: isChecklistItem(item.status) ? 'CHECKLIST' : 'FFE',
        }))
      )
    )
  );

  const insertAtCursor = useCallback((blotName, value, displayText) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
    if (range.length > 0) editor.deleteText(range.index, range.length, 'user');
    const insertAt = range.index;
    // Insert plain text first, then format that range with the blot
    editor.insertText(insertAt, displayText, 'user');
    editor.formatText(insertAt, displayText.length, blotName, value, 'user');
    // Insert a trailing space WITHOUT the blot format so the user can keep typing normally
    editor.insertText(insertAt + displayText.length, ' ', { [blotName]: false }, 'user');
    editor.setSelection(insertAt + displayText.length + 1, 0);
    editor.focus();

    // Visual feedback #1: flash animation on the newly-inserted pill
    requestAnimationFrame(() => {
      const suffix = blotName === 'tradeTag' ? 'trade' : blotName === 'productTag' ? 'product' : 'person';
      const pills = editor.root.querySelectorAll(`.qe-tag-${suffix}`);
      const newPill = pills[pills.length - 1];
      if (newPill) {
        newPill.classList.add('qe-pill-flash');
        setTimeout(() => newPill.classList.remove('qe-pill-flash'), 900);
      }
    });

    // Visual feedback #2: toast confirmation
    const label = blotName === 'tradeTag' ? `#${value.trade || value}`
      : blotName === 'productTag' ? `🏷 ${value.name || displayText}`
      : `@${value.name || displayText}`;
    setToast({ text: `Inserted ${label}`, kind: blotName, ts: Date.now() });
  }, []);

  const insertTrade = (trade) => {
    const color = TRADE_COLORS[trade] || '#6B7280';
    insertAtCursor('tradeTag', { trade, color }, `#${trade}`);
    setShowTradeMenu(false);
    setTradeFilter('');
  };
  const insertProduct = (p) => {
    const prefix = p.source === 'CHECKLIST' ? '✓' : '🏷';
    insertAtCursor('productTag', { id: p.id, name: p.name, room: p.room, source: p.source }, `${prefix} ${p.name}`);
    setShowProductMenu(false);
    setProductFilter('');
  };
  const insertPerson = (c) => {
    const name = c.username || c.name;
    insertAtCursor('personTag', { name, role: c.role || '' }, `@${name}`);
    setShowPersonMenu(false);
  };
  const insertRoomHeading = (room) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
    let insertAt = range.index;
    const before = editor.getText(Math.max(0, insertAt - 1), 1);
    if (before && before !== '\n' && insertAt > 0) {
      editor.insertText(insertAt, '\n', 'user');
      insertAt += 1;
    }
    const heading = room.name.toUpperCase();
    const DELTA_KEY = 'delta';
    const Delta = Quill.import(DELTA_KEY);
    const delta = new Delta()
      .retain(insertAt)
      .insert(heading)
      .insert('\n', { header: 2 })
      .insert('\n', { header: false });
    editor.updateContents(delta, 'user');
    const cursorPos = insertAt + heading.length + 2;
    editor.setSelection(cursorPos, 0);
    // Explicitly clear any header format on the cursor's line so typing starts fresh
    editor.formatLine(cursorPos, 0, 'header', false, 'user');
    editor.focus();
    setShowRoomMenu(false);
  };

  // ============================================================
  // Detect the current room context (most recent H1/H2/H3 above cursor)
  // so the product picker can scope to ONLY that room.
  // ============================================================
  const getCurrentRoomContext = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return null;
    const sel = editor.getSelection();
    if (!sel) return null;
    try {
      const [leaf] = editor.getLeaf(sel.index);
      if (!leaf || !leaf.domNode) return null;
      // Walk up to a direct child of editor.root
      let block = leaf.domNode;
      while (block && block.nodeType === 3) block = block.parentNode;
      while (block && block.parentNode && block.parentNode !== editor.root) block = block.parentNode;
      if (!block) return null;
      // Walk previous siblings searching for an H1/H2/H3
      let prev = block.previousElementSibling;
      while (prev) {
        if (/^H[123]$/.test(prev.tagName)) {
          return (prev.textContent || '').trim().toUpperCase();
        }
        prev = prev.previousElementSibling;
      }
    } catch { /* noop */ }
    return null;
  };

  // ============================================================
  // INLINE AUTOCOMPLETE — triggered by typing # or @
  // ============================================================
  const checkMention = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection();
    if (!range || range.length > 0) { setMention(null); return; }

    const cursor = range.index;
    const text = editor.getText(0, cursor);
    const lineStart = text.lastIndexOf('\n') + 1;
    const lineText = text.slice(lineStart);

    // Match #word or @word at the end of current line.
    // Trigger only when # / @ is at line start OR preceded by whitespace.
    const match = lineText.match(/(^|\s)([#@])([\w\-& ]{0,30})$/);
    if (!match) { setMention(null); return; }

    const triggerChar = match[2];
    const query = match[3];
    const triggerIndex = lineStart + (lineText.length - query.length - 1);
    const type = triggerChar === '#' ? 'unified' : 'person';

    // Bounds relative to editor root. Convert to viewport coords so the
    // popup can be rendered fixed-position and follow the cursor even when
    // the page is scrolled.
    const bounds = editor.getBounds(cursor);
    const rootRect = editor.root.getBoundingClientRect();

    let results = [];
    let scopedRoom = null;
    if (type === 'unified') {
      const q = query.trim().toUpperCase();
      // Trades first
      const tradeResults = allTrades
        .filter(t => !q || t.includes(q) || t.replace(/\s/g, '').includes(q.replace(/\s/g, '')))
        .map(t => ({ kind: 'trade', value: t, label: t, color: TRADE_COLORS[t] || '#6B7280' }));
      // Products next — STRICTLY filtered to current room context if one is detected
      scopedRoom = getCurrentRoomContext();
      const qLower = query.trim().toLowerCase();
      const allProductsForLine = scopedRoom
        ? allProducts.filter(p => (p.room || '').toUpperCase() === scopedRoom)
        : allProducts;
      const productResults = allProductsForLine
        .filter(p => !qLower || (p.name || '').toLowerCase().includes(qLower))
        .map(p => ({ kind: 'product', value: p, label: p.name, source: p.source, room: p.room }))
        .sort((a, b) => (a.source === b.source ? 0 : a.source === 'FFE' ? -1 : 1));
      results = [...tradeResults, ...productResults].slice(0, 12);
    } else {
      const q = query.trim().toLowerCase();
      results = (contacts || [])
        .filter(c => !q || ((c.username || c.name || '').toLowerCase().includes(q) || (c.role || '').toLowerCase().includes(q)))
        .map(c => ({ kind: 'person', value: c, label: c.username || c.name, role: c.role || '' }))
        .slice(0, 12);
    }

    if (results.length === 0) { setMention(null); return; }

    setMention({
      type, query, triggerIndex, results, selectedIndex: 0,
      scopedRoom: scopedRoom || null,
      // Viewport coords (for position: fixed popup)
      top: rootRect.top + bounds.top + bounds.height + 6,
      left: Math.min(rootRect.left + bounds.left, window.innerWidth - 340),
    });
  }, [allTrades, allProducts, contacts]);

  const selectMention = useCallback((result) => {
    const editor = quillRef.current?.getEditor();
    if (!editor || !result) return;
    const m = mentionRef.current;
    if (!m) return;
    // Compute cursor from saved triggerIndex + current query length.
    // We CANNOT rely on editor.getSelection() here because clicking the
    // popup steals focus from the contenteditable and getSelection() returns null.
    const cursorAtTrigger = m.triggerIndex + 1 + (m.query || '').length; // 1 for the # / @ char
    const deleteLen = cursorAtTrigger - m.triggerIndex;
    if (deleteLen > 0) editor.deleteText(m.triggerIndex, deleteLen, 'user');
    editor.setSelection(m.triggerIndex, 0);
    editor.focus();
    if (result.kind === 'trade') insertTrade(result.value);
    else if (result.kind === 'product') insertProduct(result.value);
    else if (result.kind === 'person') insertPerson(result.value);
    setMention(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keydown listener (capture) on editor root — handles arrow keys, Enter, Tab, Esc when popup open
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const root = editor.root;
    const onKey = (e) => {
      const m = mentionRef.current;
      if (!m) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault(); e.stopPropagation();
        setMention(prev => prev ? ({ ...prev, selectedIndex: (prev.selectedIndex + 1) % prev.results.length }) : null);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault(); e.stopPropagation();
        setMention(prev => prev ? ({ ...prev, selectedIndex: (prev.selectedIndex - 1 + prev.results.length) % prev.results.length }) : null);
      } else if (e.key === 'Tab') {
        // Tab = select highlighted result (keep this so power users get fast insert)
        e.preventDefault(); e.stopPropagation();
        selectMention(m.results[m.selectedIndex]);
      } else if (e.key === 'Enter') {
        // Enter just closes the popup so the user gets a normal newline.
        // Selecting a result is done with Tab or mouse click — Enter is for newlines.
        setMention(null);
        // Don't preventDefault — let Quill handle the Enter as a regular newline.
      } else if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation();
        setMention(null);
      }
    };
    root.addEventListener('keydown', onKey, true);
    return () => root.removeEventListener('keydown', onKey, true);
  }, [selectMention]);

  // Close menus on outside click
  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest('.tag-menu') && !e.target.closest('.tag-trigger')) {
        setShowTradeMenu(false);
        setShowProductMenu(false);
        setShowPersonMenu(false);
        setShowRoomMenu(false);
      }
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  // ============================================================
  // HOVER PREVIEW — show live thumbnail/info for pills inside the editor
  // ============================================================
  const [hoverPill, setHoverPill] = useState(null); // { kind, data, top, left }

  // Build id → item lookup for live resolution
  const itemsById = {};
  rooms.forEach(r => {
    (r.categories || []).forEach(c => {
      (c.subcategories || []).forEach(sub => {
        (sub.items || []).forEach(item => {
          itemsById[item.id] = { ...item, _room: r.name, _roomColor: r.color };
        });
      });
    });
  });

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const root = editor.root;
    const onOver = (e) => {
      const pill = e.target.closest('[data-tag]');
      if (!pill) { setHoverPill(null); return; }
      const r = pill.getBoundingClientRect();
      const top = r.bottom + 6;
      const left = Math.min(r.left, window.innerWidth - 280);
      const tagType = pill.getAttribute('data-tag');
      if (tagType === 'product') {
        const id = pill.getAttribute('data-product-id');
        const fallbackName = pill.getAttribute('data-product-name');
        const live = itemsById[id];
        setHoverPill({ kind: 'product', data: live || { _missing: true, id, name: fallbackName }, top, left });
      } else if (tagType === 'trade') {
        setHoverPill({ kind: 'trade', data: { trade: pill.getAttribute('data-trade'), color: pill.getAttribute('data-color') }, top, left });
      } else if (tagType === 'person') {
        const name = pill.getAttribute('data-person');
        const contact = (contacts || []).find(c => (c.username || c.name) === name);
        setHoverPill({ kind: 'person', data: { name, role: pill.getAttribute('data-role'), contact }, top, left });
      }
    };
    const onOut = (e) => {
      if (!e.relatedTarget || !e.relatedTarget.closest || !e.relatedTarget.closest('[data-tag]')) {
        setHoverPill(null);
      }
    };
    root.addEventListener('mouseover', onOver);
    root.addEventListener('mouseout', onOut);
    return () => {
      root.removeEventListener('mouseover', onOver);
      root.removeEventListener('mouseout', onOut);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, contacts]);

  return (
    <div className="scope-doc-editor" data-testid="scope-document-editor">
      <style>{`
        .scope-doc-editor .ql-toolbar {
          background: #1a1f2e;
          border-color: #2a3040 !important;
          border-radius: 8px 8px 0 0;
          position: sticky;
          top: 0;
          z-index: 30;
        }
        .scope-doc-editor .ql-container {
          background: #0f1218;
          border-color: #2a3040 !important;
          color: #F5F5DC;
          font-size: 15px;
          min-height: 380px;
          border-radius: 0;
        }
        .scope-doc-editor .ql-editor { color: #F5F5DC; padding: 16px 20px; line-height: 1.7; min-height: 380px; }
        .scope-doc-editor .ql-editor.ql-blank::before { color: #4B5563 !important; font-style: normal !important; }
        .scope-doc-editor .ql-editor h1 { color: #D4A574; font-size: 28px; font-weight: 900; letter-spacing: 2px; text-transform: uppercase; margin-top: 16px; padding-bottom: 4px; border-bottom: 2px solid #D4A574; }
        .scope-doc-editor .ql-editor h2 { color: #D4A574; font-size: 22px; font-weight: 900; letter-spacing: 3px; text-transform: uppercase; margin-top: 20px; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #D4A574; }
        .scope-doc-editor .ql-editor h3 { color: #D4A574; font-size: 18px; font-weight: 800; letter-spacing: 1px; margin-top: 14px; }
        .scope-doc-editor .ql-editor ol, .scope-doc-editor .ql-editor ul { padding-left: 1.5em; }
        .scope-doc-editor .ql-editor li { margin: 4px 0; }
        .scope-doc-editor .ql-toolbar button { color: #9CA3AF !important; }
        .scope-doc-editor .ql-toolbar button:hover { color: #D4A574 !important; }
        .scope-doc-editor .ql-toolbar button.ql-active { color: #D4A574 !important; }
        .scope-doc-editor .ql-toolbar .ql-stroke { stroke: #9CA3AF !important; }
        .scope-doc-editor .ql-toolbar button:hover .ql-stroke { stroke: #D4A574 !important; }
        .scope-doc-editor .ql-toolbar button.ql-active .ql-stroke { stroke: #D4A574 !important; }
        .scope-doc-editor .ql-toolbar .ql-fill { fill: #9CA3AF !important; }
        .scope-doc-editor .ql-toolbar button:hover .ql-fill { fill: #D4A574 !important; }
        .scope-doc-editor .ql-toolbar .ql-picker-label { color: #9CA3AF !important; }
        .scope-doc-editor .ql-toolbar .ql-picker-options { background: #1a1f2e !important; border-color: #2a3040 !important; }
        .scope-doc-editor .ql-toolbar .ql-picker-item { color: #F5F5DC !important; }
        .scope-doc-editor .qe-tag-trade,
        .scope-doc-editor .qe-tag-product,
        .scope-doc-editor .qe-tag-person { user-select: none; cursor: default; padding: 1px 8px; border-radius: 4px; font-weight: 800; font-size: 0.85em; margin: 0 2px; display: inline-block; line-height: 1.4; }
        .scope-doc-editor .qe-tag-trade { background: var(--pill-bg, #6B7280); color: #fff; letter-spacing: 1px; }
        .scope-doc-editor .qe-tag-product { background: #1E3A5F; color: #93C5FD; border: 1px solid #3B82F6; font-weight: 700; }
        .scope-doc-editor .qe-tag-person { background: rgba(212,165,116,0.2); color: #D4A574; border: 1px solid #D4A574; font-weight: 700; }
        /* Flash animation when a new pill is inserted — gives instant visual feedback */
        @keyframes qe-flash {
          0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.9); transform: scale(1); }
          20% { box-shadow: 0 0 0 8px rgba(255, 255, 255, 0.4); transform: scale(1.15); }
          60% { box-shadow: 0 0 0 14px rgba(255, 255, 255, 0); transform: scale(1.05); }
          100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); transform: scale(1); }
        }
        .scope-doc-editor .qe-pill-flash { animation: qe-flash 0.9s ease-out; z-index: 10; position: relative; }
        /* Read-only render in builder portal */
        .scope-doc-readonly .qe-tag-trade,
        .scope-doc-readonly .qe-tag-product,
        .scope-doc-readonly .qe-tag-person { padding: 1px 8px; border-radius: 4px; font-weight: 800; font-size: 0.85em; margin: 0 2px; display: inline-block; line-height: 1.4; }
        .scope-doc-readonly .qe-tag-trade { background: var(--pill-bg, #6B7280); color: #fff; letter-spacing: 1px; }
        .scope-doc-readonly .qe-tag-product { background: #1E3A5F; color: #93C5FD; border: 1px solid #3B82F6; font-weight: 700; }
        .scope-doc-readonly .qe-tag-person { background: rgba(212,165,116,0.2); color: #D4A574; border: 1px solid #D4A574; font-weight: 700; }
        .tag-toolbar {
          display: flex; gap: 8px; flex-wrap: wrap; padding: 8px 10px;
          background: #1a1f2e; border-left: 1px solid #2a3040; border-right: 1px solid #2a3040;
        }
        /* Fixed-position "current room" indicator — shown when scrolling
           through the editor so the user always knows which room they're in */
        .current-room-bar {
          position: fixed;
          top: 8px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 99998;
          padding: 8px 18px;
          background: #0f1218;
          border: 2px solid var(--room-color, #D4A574);
          border-radius: 8px;
          color: #9CA3AF;
          font-size: 11px;
          letter-spacing: 1.5px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 10px;
          box-shadow: 0 6px 20px rgba(0,0,0,0.6);
          pointer-events: none;
        }
        .current-room-bar .room-label {
          background: var(--room-color, #D4A574);
          color: #0f1218;
          padding: 4px 12px;
          border-radius: 4px;
          font-weight: 900;
          letter-spacing: 1.5px;
          font-size: 12px;
        }
        .tag-toolbar button.tag-trigger {
          padding: 6px 12px; border-radius: 6px; font-size: 11px; font-weight: 800;
          letter-spacing: 1px; text-transform: uppercase; border: 1px solid;
          cursor: pointer; transition: all 0.15s;
        }
        .tag-toolbar button.tag-trigger:hover { transform: translateY(-1px); }
        .tag-menu {
          position: absolute; z-index: 9999; background: #0f1218;
          border: 1px solid #2a3040; border-radius: 8px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.5);
          padding: 8px; max-height: 320px; overflow-y: auto;
          min-width: 240px; max-width: 380px;
          left: 0; top: 100%; margin-top: 4px;
        }
        .tag-menu input {
          width: 100%; background: #1a1f2e; border: 1px solid #2a3040;
          border-radius: 4px; padding: 6px 8px; color: #F5F5DC;
          font-size: 12px; margin-bottom: 6px;
        }
        .tag-menu .menu-item {
          padding: 6px 8px; border-radius: 4px; cursor: pointer;
          font-size: 12px; transition: background 0.1s;
          display: flex; align-items: center; gap: 6px;
        }
        .tag-menu .menu-item:hover { background: #2a3040; }
      `}</style>

      {/* Tag Insert Toolbar */}
      <div className="tag-toolbar">
        <span style={{ color: '#6B7280', fontSize: 11, fontWeight: 700, alignSelf: 'center', marginRight: 4 }}>INSERT INLINE:</span>

        {/* Trade picker */}
        <div style={{ position: 'relative' }}>
          <button
            className="tag-trigger"
            data-testid="insert-trade-btn"
            onClick={(e) => { e.stopPropagation(); setShowTradeMenu(!showTradeMenu); setShowProductMenu(false); setShowPersonMenu(false); setShowRoomMenu(false); }}
            style={{ background: '#EAB30820', color: '#EAB308', borderColor: '#EAB30850' }}
          >
            # TRADE
          </button>
          {showTradeMenu && (
            <div className="tag-menu" data-testid="trade-menu" style={{ top: '100%', marginTop: 4 }}>
              <input
                placeholder="Search trades or type new..."
                value={tradeFilter}
                onChange={e => setTradeFilter(e.target.value.toUpperCase())}
                onKeyDown={e => {
                  if (e.key === 'Enter' && tradeFilter.trim()) {
                    const t = tradeFilter.trim();
                    if (!allTrades.includes(t) && onAddCustomTrade) onAddCustomTrade(t);
                    insertTrade(t);
                  }
                }}
                autoFocus
              />
              {allTrades.filter(t => !tradeFilter || t.includes(tradeFilter)).map(t => {
                const c = TRADE_COLORS[t] || '#A3A3A3';
                return (
                  <div key={t} className="menu-item" onClick={() => insertTrade(t)}>
                    <span style={{ background: c, width: 12, height: 12, borderRadius: 3, display: 'inline-block' }} />
                    <span style={{ color: c, fontWeight: 800 }}>{t}</span>
                  </div>
                );
              })}
              {tradeFilter && !allTrades.includes(tradeFilter.trim()) && (
                <div className="menu-item" onClick={() => {
                  const t = tradeFilter.trim();
                  if (onAddCustomTrade) onAddCustomTrade(t);
                  insertTrade(t);
                }} style={{ borderTop: '1px solid #2a3040', marginTop: 4, paddingTop: 8 }}>
                  <span style={{ color: '#10B981', fontWeight: 800 }}>+ Create &amp; insert "{tradeFilter}"</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Product picker */}
        <div style={{ position: 'relative' }}>
          <button
            className="tag-trigger"
            data-testid="insert-product-btn"
            onClick={(e) => { e.stopPropagation(); setShowProductMenu(!showProductMenu); setShowTradeMenu(false); setShowPersonMenu(false); setShowRoomMenu(false); }}
            style={{ background: '#3B82F620', color: '#93C5FD', borderColor: '#3B82F650' }}
          >
            🏷 PRODUCT
          </button>
          {showProductMenu && (
            <div className="tag-menu" data-testid="product-menu" style={{ top: '100%', marginTop: 4 }}>
              <input
                placeholder="Search FFE or Checklist items..."
                value={productFilter}
                onChange={e => setProductFilter(e.target.value)}
                autoFocus
              />
              {(() => {
                const roomCtx = getCurrentRoomContext();
                if (roomCtx) {
                  return (
                    <div style={{ fontSize: 9, color: '#10B981', fontWeight: 800, letterSpacing: 1.2, padding: '4px 6px', background: '#10B98115', marginBottom: 4, borderRadius: 4 }}>
                      🔒 SCOPED TO ROOM: {roomCtx}
                    </div>
                  );
                }
                return null;
              })()}
              {allProducts.length === 0 && <div style={{ color: '#6B7280', fontSize: 12, padding: 8 }}>No items yet. Add items in the Walkthrough, Checklist, or FFE section first.</div>}
              {allProducts.length > 0 && (() => {
                const roomCtx = getCurrentRoomContext();
                const scoped = roomCtx
                  ? allProducts.filter(p => (p.room || '').toUpperCase() === roomCtx)
                  : allProducts;
                const filtered = scoped.filter(p => !productFilter || (p.name || '').toLowerCase().includes(productFilter.toLowerCase()) || (p.room || '').toLowerCase().includes(productFilter.toLowerCase()));
                const ffeItems = filtered.filter(p => p.source === 'FFE').slice(0, 30);
                const checklistItems = filtered.filter(p => p.source === 'CHECKLIST').slice(0, 30);
                return (
                  <>
                    {ffeItems.length > 0 && (
                      <>
                        <div style={{ color: '#3B82F6', fontSize: 9, fontWeight: 800, letterSpacing: 1.5, padding: '6px 8px 2px', borderBottom: '1px solid #1f2937' }}>🏷 FF&amp;E ITEMS</div>
                        {ffeItems.map(p => (
                          <div key={p.id} className="menu-item" onClick={() => insertProduct(p)}>
                            <span style={{ background: '#3B82F6', color: '#fff', fontSize: 8, fontWeight: 800, padding: '1px 5px', borderRadius: 3, letterSpacing: 0.5 }}>FFE</span>
                            <span style={{ color: '#93C5FD', fontWeight: 700 }}>{p.name}</span>
                            <span style={{ color: '#6B7280', fontSize: 10 }}>({p.room})</span>
                          </div>
                        ))}
                      </>
                    )}
                    {checklistItems.length > 0 && (
                      <>
                        <div style={{ color: '#10B981', fontSize: 9, fontWeight: 800, letterSpacing: 1.5, padding: '6px 8px 2px', borderBottom: '1px solid #1f2937', marginTop: ffeItems.length > 0 ? 4 : 0 }}>✓ CHECKLIST ITEMS</div>
                        {checklistItems.map(p => (
                          <div key={p.id} className="menu-item" onClick={() => insertProduct(p)}>
                            <span style={{ background: '#10B981', color: '#fff', fontSize: 8, fontWeight: 800, padding: '1px 5px', borderRadius: 3, letterSpacing: 0.5 }}>CHK</span>
                            <span style={{ color: '#86EFAC', fontWeight: 700 }}>{p.name}</span>
                            <span style={{ color: '#6B7280', fontSize: 10 }}>({p.room})</span>
                          </div>
                        ))}
                      </>
                    )}
                    {ffeItems.length === 0 && checklistItems.length === 0 && (
                      <div style={{ color: '#6B7280', fontSize: 12, padding: 8 }}>
                        {roomCtx
                          ? `No items in "${roomCtx}". Add items there first or remove the room heading.`
                          : `No items match "${productFilter}"`}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Person picker */}
        <div style={{ position: 'relative' }}>
          <button
            className="tag-trigger"
            data-testid="insert-person-btn"
            onClick={(e) => { e.stopPropagation(); setShowPersonMenu(!showPersonMenu); setShowTradeMenu(false); setShowProductMenu(false); setShowRoomMenu(false); }}
            style={{ background: '#D4A57420', color: '#D4A574', borderColor: '#D4A57480' }}
          >
            @ PERSON
          </button>
          {showPersonMenu && (
            <div className="tag-menu" data-testid="person-menu" style={{ top: '100%', marginTop: 4 }}>
              {contacts.length === 0 && <div style={{ color: '#6B7280', fontSize: 12, padding: 8 }}>No contacts yet. Add contacts in the section below first.</div>}
              {contacts.map((c, i) => (
                <div key={i} className="menu-item" onClick={() => insertPerson(c)}>
                  <span style={{ color: '#D4A574', fontWeight: 700 }}>@{c.username || c.name}</span>
                  <span style={{ color: '#6B7280', fontSize: 10 }}>({c.role})</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Room heading inserter (creates section header) */}
        <div style={{ position: 'relative' }}>
          <button
            className="tag-trigger"
            data-testid="insert-room-btn"
            onClick={(e) => { e.stopPropagation(); setShowRoomMenu(!showRoomMenu); setShowTradeMenu(false); setShowProductMenu(false); setShowPersonMenu(false); }}
            style={{ background: '#10B98120', color: '#10B981', borderColor: '#10B98150' }}
          >
            ▼ ROOM HEADING
          </button>
          {showRoomMenu && (
            <div className="tag-menu" data-testid="room-menu" style={{ top: '100%', marginTop: 4 }}>
              {rooms.length === 0 && <div style={{ color: '#6B7280', fontSize: 12, padding: 8 }}>No rooms in this project.</div>}
              {rooms.map(r => (
                <div key={r.id} className="menu-item" onClick={() => insertRoomHeading(r)}>
                  <span style={{ background: r.color || '#D4A574', width: 12, height: 12, borderRadius: 3, display: 'inline-block' }} />
                  <span style={{ color: r.color || '#D4A574', fontWeight: 800 }}>{r.name.toUpperCase()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Custom trade quick-add */}
        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto', alignItems: 'center' }}>
          <input
            value={newTradeName || ''}
            onChange={e => setNewTradeName && setNewTradeName(e.target.value.toUpperCase())}
            placeholder="+ Custom trade..."
            data-testid="custom-trade-input"
            style={{ background: '#0f1218', border: '1px solid #2a3040', borderRadius: 4, padding: '4px 8px', color: '#EAB308', fontSize: 11, width: 160 }}
            onKeyDown={e => {
              if (e.key === 'Enter' && (newTradeName || '').trim() && onAddCustomTrade) {
                onAddCustomTrade(newTradeName.trim());
                setNewTradeName('');
              }
            }}
          />
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        {/* Fixed "current room" indicator — portal-rendered so it floats above everything */}
        {currentRoom && ReactDOM.createPortal(
          <div
            className="current-room-bar"
            style={{ '--room-color': currentRoom.color }}
            data-testid="current-room-bar"
          >
            <span style={{ opacity: 0.7 }}>YOU ARE IN:</span>
            <span className="room-label">{currentRoom.name}</span>
          </div>,
          document.body
        )}
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          onChange={(content, delta, source) => {
            if (source === 'user' && onChange) onChange(content);
            // Defer to allow Quill to settle the selection before checking
            setTimeout(checkMention, 0);
          }}
          onChangeSelection={() => { setTimeout(checkMention, 0); }}
          modules={MODULES}
          formats={FORMATS}
          placeholder="Start typing your scope of work...

Tip: Type # for trades & products (autocomplete), @ for people. Use ▼ ROOM HEADING button for room sections.

Example:
  FOYER  (heading)
  1. Paint entire foyer #PAINT
  2. Hang new chandelier #ELECTRICAL #Chandelier (auto-suggests products + trades)
  3. Box in beams w/ white oak #TRIM CARPENTER @JoeContractor"
        />

        {/* INLINE AUTOCOMPLETE POPUP — rendered via portal with fixed viewport coords */}
        {mention && mention.results.length > 0 && ReactDOM.createPortal(
          <div
            data-testid="mention-popup"
            style={{
              position: 'fixed',
              top: mention.top,
              left: mention.left,
              zIndex: 99999,
              background: '#0f1218',
              border: '1px solid #2a3040',
              borderRadius: 8,
              boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
              padding: 4,
              minWidth: 240,
              maxWidth: 360,
              maxHeight: 280,
              overflowY: 'auto',
            }}
          >
            <div style={{ fontSize: 9, color: '#6B7280', fontWeight: 800, letterSpacing: 1.2, padding: '4px 6px', borderBottom: '1px solid #1f2937' }}>
              {mention.type === 'unified'
                ? '↑↓ NAVIGATE · TAB OR CLICK SELECT · ENTER NEW LINE · ESC CANCEL'
                : '↑↓ PEOPLE · TAB OR CLICK SELECT · ENTER NEW LINE · ESC CANCEL'}
            </div>
            {mention.scopedRoom && (
              <div style={{ fontSize: 9, color: '#10B981', fontWeight: 800, letterSpacing: 1.2, padding: '4px 6px', background: '#10B98115' }}>
                🔒 SCOPED TO ROOM: {mention.scopedRoom}
              </div>
            )}
            {mention.results.map((r, idx) => {
              const selected = idx === mention.selectedIndex;
              if (r.kind === 'trade') {
                return (
                  <div
                    key={`t-${r.value}`}
                    onMouseDown={(e) => { e.preventDefault(); selectMention(r); }}
                    onMouseEnter={() => setMention(prev => prev ? { ...prev, selectedIndex: idx } : null)}
                    style={{
                      padding: '6px 8px', borderRadius: 4, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: selected ? '#1F2937' : 'transparent',
                    }}
                    data-testid={`mention-result-${idx}`}
                  >
                    <span style={{ background: r.color, color: '#fff', fontSize: 8, fontWeight: 800, padding: '1px 5px', borderRadius: 3, letterSpacing: 0.5 }}>TRADE</span>
                    <span style={{ color: r.color, fontWeight: 800, fontSize: 12 }}>#{r.label}</span>
                  </div>
                );
              }
              if (r.kind === 'product') {
                const isCheck = r.source === 'CHECKLIST';
                return (
                  <div
                    key={`p-${r.value.id}`}
                    onMouseDown={(e) => { e.preventDefault(); selectMention(r); }}
                    onMouseEnter={() => setMention(prev => prev ? { ...prev, selectedIndex: idx } : null)}
                    style={{
                      padding: '6px 8px', borderRadius: 4, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: selected ? '#1F2937' : 'transparent',
                    }}
                    data-testid={`mention-result-${idx}`}
                  >
                    <span style={{ background: isCheck ? '#10B981' : '#3B82F6', color: '#fff', fontSize: 8, fontWeight: 800, padding: '1px 5px', borderRadius: 3, letterSpacing: 0.5 }}>{isCheck ? 'CHK' : 'FFE'}</span>
                    <span style={{ color: isCheck ? '#86EFAC' : '#93C5FD', fontWeight: 700, fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
                    <span style={{ color: '#6B7280', fontSize: 10 }}>{r.room}</span>
                  </div>
                );
              }
              if (r.kind === 'person') {
                return (
                  <div
                    key={`u-${idx}`}
                    onMouseDown={(e) => { e.preventDefault(); selectMention(r); }}
                    onMouseEnter={() => setMention(prev => prev ? { ...prev, selectedIndex: idx } : null)}
                    style={{
                      padding: '6px 8px', borderRadius: 4, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: selected ? '#1F2937' : 'transparent',
                    }}
                    data-testid={`mention-result-${idx}`}
                  >
                    <span style={{ background: '#D4A574', color: '#1a1f2e', fontSize: 8, fontWeight: 800, padding: '1px 5px', borderRadius: 3, letterSpacing: 0.5 }}>USER</span>
                    <span style={{ color: '#D4A574', fontWeight: 800, fontSize: 12 }}>@{r.label}</span>
                    {r.role && <span style={{ color: '#6B7280', fontSize: 10 }}>({r.role})</span>}
                  </div>
                );
              }
              return null;
            })}
          </div>,
          document.body
        )}
      </div>

      {/* Custom trade chips */}
      {customTrades.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          <span style={{ color: '#6B7280', fontSize: 11 }}>Custom trades:</span>
          {customTrades.map((t, i) => (
            <span key={i} style={{ background: '#EAB30815', color: '#EAB308', border: '1px solid #EAB30850', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
              {t}
              {onRemoveCustomTrade && <span style={{ cursor: 'pointer', color: '#EF4444' }} onClick={() => onRemoveCustomTrade(t)}>×</span>}
            </span>
          ))}
        </div>
      )}

      {/* HOVER PREVIEW — follows mouse over pills inside the Quill editor */}
      {hoverPill && ReactDOM.createPortal(
        <PillHoverCard hoverPill={hoverPill} />,
        document.body
      )}

      {/* TOAST — quick confirmation when a pill is inserted */}
      {toast && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 999999,
            background: toast.kind === 'tradeTag' ? '#10B981' : toast.kind === 'productTag' ? '#3B82F6' : '#D4A574',
            color: toast.kind === 'personTag' ? '#1a1f2e' : '#fff',
            padding: '10px 18px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 800,
            letterSpacing: 0.5,
            boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
            animation: 'qe-toast-in 0.25s ease-out',
            pointerEvents: 'none',
          }}
          data-testid="scope-toast"
        >
          <style>{`@keyframes qe-toast-in { from { opacity: 0; transform: translateX(-50%) translateY(12px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }`}</style>
          ✓ {toast.text}
        </div>,
        document.body
      )}
    </div>
  );
}

// Floating preview card for pills inside the editor
function PillHoverCard({ hoverPill }) {
  const { kind, data, top, left } = hoverPill;
  const base = {
    position: 'fixed', top, left, zIndex: 99999,
    background: '#0f1218', border: '1px solid #2a3040', borderRadius: 8,
    boxShadow: '0 12px 40px rgba(0,0,0,0.7)', pointerEvents: 'none', overflow: 'hidden', width: 260,
  };
  if (kind === 'product') {
    if (data._missing) {
      return (
        <div style={base} data-testid="pill-hover-missing">
          <div style={{ padding: 12 }}>
            <div style={{ color: '#EF4444', fontSize: 11, fontWeight: 800, marginBottom: 4 }}>ITEM NOT FOUND</div>
            <div style={{ color: '#9CA3AF', fontSize: 11 }}>Was: <strong>{data.name}</strong></div>
            <div style={{ color: '#6B7280', fontSize: 10, marginTop: 4 }}>It may have been deleted. Tag will still update if re-added.</div>
          </div>
        </div>
      );
    }
    const CHK = new Set(['ORDER SAMPLES','SAMPLES ARRIVED','ASK NEIL','ASK CHARLENE','ASK JALA','GET QUOTE','WAITING ON QT','READY FOR PRESENTATION','TO BE SELECTED','RESEARCHING','PENDING APPROVAL','', null]);
    const isChk = CHK.has(data.status || '');
    return (
      <div style={base} data-testid="pill-hover-product">
        {data.image_url ? (
          <img src={data.image_url} alt={data.name} style={{ width: '100%', height: 180, objectFit: 'cover', background: '#0f1218' }} />
        ) : (
          <div style={{ width: '100%', height: 60, background: '#1a1f2e', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4B5563', fontSize: 11, fontStyle: 'italic' }}>No photo yet</div>
        )}
        <div style={{ padding: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{ background: isChk ? '#10B981' : '#3B82F6', color: '#fff', fontSize: 8, fontWeight: 800, padding: '1px 6px', borderRadius: 3, letterSpacing: 0.5 }}>{isChk ? 'CHK' : 'FFE'}</span>
            {data._room && <span style={{ color: data._roomColor || '#D4A574', fontSize: 10, fontWeight: 700, letterSpacing: 0.8 }}>{data._room}</span>}
          </div>
          <div style={{ color: '#F5F5DC', fontSize: 13, fontWeight: 800, marginBottom: 4, lineHeight: 1.25 }}>{data.name || '(unnamed)'}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 10, color: '#9CA3AF' }}>
            {data.vendor && <span>{data.vendor}</span>}
            {data.sku && <span>SKU: {data.sku}</span>}
            {data.size && <span>{data.size}</span>}
            {data.finish_color && <span>{data.finish_color}</span>}
          </div>
          {data.status && <div style={{ marginTop: 6, fontSize: 10, color: '#D4A574', fontWeight: 700 }}>● {data.status}</div>}
        </div>
      </div>
    );
  }
  if (kind === 'trade') {
    return (
      <div style={{ ...base, width: 200 }} data-testid="pill-hover-trade">
        <div style={{ padding: 12 }}>
          <div style={{ display: 'inline-block', background: data.color, color: '#fff', fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 4, letterSpacing: 1, marginBottom: 6 }}>#{data.trade}</div>
          <div style={{ color: '#9CA3AF', fontSize: 11 }}>Trade category</div>
          <div style={{ color: '#6B7280', fontSize: 10, marginTop: 4 }}>Click tag in preview to filter all items by this trade.</div>
        </div>
      </div>
    );
  }
  if (kind === 'person') {
    const c = data.contact;
    return (
      <div style={{ ...base, width: 240 }} data-testid="pill-hover-person">
        <div style={{ padding: 12 }}>
          <div style={{ color: '#D4A574', fontSize: 13, fontWeight: 800, marginBottom: 2 }}>@{data.name}</div>
          {data.role && <div style={{ color: '#9CA3AF', fontSize: 11, marginBottom: 6 }}>{data.role}</div>}
          {c ? (
            <div style={{ fontSize: 11, color: '#E5E7EB' }}>
              {c.name && <div>{c.name}</div>}
              {c.phone && <div style={{ color: '#9CA3AF' }}>{c.phone}</div>}
              {c.email && <div style={{ color: '#9CA3AF' }}>{c.email}</div>}
            </div>
          ) : (
            <div style={{ fontSize: 10, color: '#6B7280', fontStyle: 'italic' }}>Add to Project Contacts to see details here.</div>
          )}
        </div>
      </div>
    );
  }
  return null;
}

// =================================================================
// PARSER — Extract tagged items from the scope document HTML
// Returns: { byRoom: { roomName: [{html, trades, products, people}] },
//           byTrade: { trade: [{html, room, products, people}] },
//           anyTags: bool }
// Strategy: walk top-level block elements; track current room from H1/H2/H3.
// Each block (p / li / div) becomes one "item". Items are bucketed by
// trade tags found inside, and by current room context.
// =================================================================
export function parseScopeDocument(html, rooms = []) {
  const result = { byRoom: {}, byTrade: {}, anyTags: false, items: [] };
  if (!html || !html.trim()) return result;

  // Build a set of known room names for matching headings
  const roomNameMap = {};
  rooms.forEach(r => { if (r && r.name) roomNameMap[r.name.trim().toUpperCase()] = r; });

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstChild;
  if (!root) return result;

  let currentRoom = null;
  let currentRoomName = 'GENERAL';

  // Recursively flatten <ol>/<ul>/blockquote children to <li>/<p>/<div> level
  // For simplicity, iterate top-level children and recurse into list containers.
  const flushBlock = (block) => {
    if (!block) return;
    // Get the block's text/HTML
    const blockHtml = block.innerHTML.trim();
    if (!blockHtml) return;

    // Check if this is a heading (sets current room context)
    const tag = (block.tagName || '').toUpperCase();
    if (tag === 'H1' || tag === 'H2' || tag === 'H3') {
      const headingText = (block.textContent || '').trim().toUpperCase();
      currentRoomName = headingText || 'GENERAL';
      currentRoom = roomNameMap[headingText] || null;
      return; // headings are not items
    }

    // Skip empty/whitespace-only paragraphs
    if (!block.textContent || !block.textContent.trim()) return;

    // Find all tags in this block
    const tradeNodes = block.querySelectorAll('[data-tag="trade"]');
    const productNodes = block.querySelectorAll('[data-tag="product"]');
    const personNodes = block.querySelectorAll('[data-tag="person"]');
    const trades = Array.from(tradeNodes).map(n => n.getAttribute('data-trade')).filter(Boolean);
    const products = Array.from(productNodes).map(n => ({
      id: n.getAttribute('data-product-id'),
      name: n.getAttribute('data-product-name'),
      room: n.getAttribute('data-product-room'),
    }));
    const people = Array.from(personNodes).map(n => ({
      name: n.getAttribute('data-person'),
      role: n.getAttribute('data-role'),
    }));

    if (trades.length || products.length || people.length) result.anyTags = true;

    const item = {
      html: blockHtml,
      text: (block.textContent || '').trim(),
      tag,
      trades,
      products,
      people,
      roomName: currentRoomName,
      room: currentRoom,
    };
    result.items.push(item);

    // Bucket By Room
    if (!result.byRoom[currentRoomName]) result.byRoom[currentRoomName] = { room: currentRoom, items: [] };
    result.byRoom[currentRoomName].items.push(item);

    // Bucket By Trade (one item can appear under multiple trades)
    if (trades.length === 0) {
      const key = '__UNTAGGED__';
      if (!result.byTrade[key]) result.byTrade[key] = [];
      result.byTrade[key].push(item);
    } else {
      trades.forEach(t => {
        if (!result.byTrade[t]) result.byTrade[t] = [];
        result.byTrade[t].push(item);
      });
    }
  };

  const walk = (node) => {
    if (!node) return;
    const tag = (node.tagName || '').toUpperCase();
    if (tag === 'OL' || tag === 'UL') {
      Array.from(node.children).forEach(walk);
    } else if (tag === 'LI' || tag === 'P' || tag === 'DIV' || tag === 'BLOCKQUOTE' ||
               tag === 'H1' || tag === 'H2' || tag === 'H3') {
      flushBlock(node);
    } else if (node.nodeType === 1) {
      // unknown element - try its children
      Array.from(node.children).forEach(walk);
    }
  };

  Array.from(root.children).forEach(walk);
  return result;
}
